<template>
  <div
    :class="
      `popup__block popup__block--${variant} popup__block--${theme} popup__block--img-${heroImageComputedPosition}`
    "
    :style="blockStyles"
  >
    <div
      v-if="heroImageIsVisible"
      ref="heroImage"
      :class="`popup__hero-image popup__hero-image--${heroImageComputedPosition}`"
      :style="`background-image: url('${heroImageDisplayed}');`"
    ></div>
    <div :class="['popup__content', { 'popup__content--full-height': isFullHeight }]">
      <div
        ref="contentInner"
        :class="`popup__content-inner popup__content-inner--img-${heroImageComputedPosition}`"
      >
        <h2 v-if="header" :class="`popup__header popup__header--${variant}`" v-html="header"></h2>
        <div
          v-if="subheader"
          :class="`popup__subheader popup__subheader--${variant} popup__subheader--${theme}`"
          v-html="subheader"
        ></div>

        <component :is="dynamicComponent" v-if="childComponentPath"></component>

        <div v-else-if="!!buttons.length" class="popup__cta-wrap">
          <a
            v-for="(button, index) in buttons"
            :key="index"
            class="cta cta--button cta--blue popup__cta"
            :href="button.link ? button.link : false"
            v-on="button.onclick ? { click: button.onclick } : {}"
            >{{ button.text }}</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { updateCloseButtonTheme } from '../dy-modal-override'

export default {
  name: 'PopupBlock',

  props: {
    showOverlay: { type: Function, required: true },
  },

  data() {
    return {
      imgWinHeightBreakpoint: 700,
      variants: On.Popup.variants,
      heroImagePositions: On.Popup.heroImagePositions,
      blockHeight: 0,
    }
  },

  computed: {
    ...mapGetters({
      variant: 'popup/variant',
      theme: 'popup/theme',
      closeBtnColorIfOnImg: 'popup/closeBtnColorIfOnImg',
      header: 'popup/header',
      subheader: 'popup/subheader',
      buttons: 'popup/buttons',
      heroImages: 'popup/heroImages',
      heroImagePosition: 'popup/heroImagePosition',
      childComponentPath: 'popup/childComponentPath',
      isVisible: 'popup/isVisible',
      initializedAt: 'popup/initializedAt',
      initDelay: 'popup/initDelay',
      winSize: 'viewport/dimensions',
      winHeight: 'viewport/height',
      isMqSmall: 'viewport/isMqSmall',
      isMqLarger: 'viewport/isMqLarger',
    }),

    dynamicComponent() {
      return () => import(`@/components/${this.childComponentPath}`)
    },

    heroImageComputedPosition() {
      const position = this.heroImagePosition
      const positions = this.heroImagePositions
      if (!position || position == positions.noImage) return 'none'

      return position == positions.topOrLeft && this.winHeight >= this.imgWinHeightBreakpoint
        ? 'top'
        : 'left'
    },

    heroImageDisplayed() {
      let imgIndex = 0
      if (this.heroImageComputedPosition == 'left') {
        if (!this.isMqLarger) return null
        imgIndex = 1
      }
      return this.heroImages[imgIndex] || this.heroImages[0]
    },

    heroImageIsVisible() {
      return this.heroImageComputedPosition != 'none' && !!this.heroImageDisplayed
    },

    blockStyles() {
      if (!this.isVisible || !this.blockHeight) return {}

      const isMinWindowHeight = this.winHeight < this.blockHeight + 40
      return !this.isMqSmall || isMinWindowHeight ? { height: `${this.winHeight}px` } : {}
    },

    isFullHeight() {
      return !!this.blockStyles.height && this.winHeight < this.blockHeight
    },
  },

  watch: {
    theme() {
      this.updateCloseButtonTheme()
    },

    heroImageComputedPosition: {
      immediate: true,
      handler() {
        this.calculateBlockHeight()
        this.updateCloseButtonTheme()
        this.trackIfUserHasSeenAnImage()
      },
    },

    isVisible() {
      this.calculateBlockHeight()
    },

    winSize() {
      this.calculateBlockHeight()
    },
  },

  mounted() {
    imagesLoaded(this.$el, { background: true }, () => {
      let timeLeftBeforeInit = 0

      if (this.initializedAt && this.initDelay) {
        const now = new Date().getTime()
        timeLeftBeforeInit = this.initDelay - (now - this.initializedAt)
      }
      timeLeftBeforeInit > 0
        ? setTimeout(() => this.showOverlay(), timeLeftBeforeInit)
        : this.showOverlay()
    })
  },

  methods: {
    calculateBlockHeight() {
      if (!this.isVisible || !this.$refs.contentInner) return

      const minHeight = 370
      const contentInnerHeight = this.$refs.contentInner.offsetHeight
      const contentHeight = contentInnerHeight < minHeight ? minHeight : contentInnerHeight
      const heroImageHeight =
        this.heroImageComputedPosition == 'top' && this.$refs.heroImage
          ? this.$refs.heroImage.offsetHeight
          : 0
      this.blockHeight = heroImageHeight + contentHeight
    },

    updateCloseButtonTheme() {
      const btnTheme =
        this.heroImageComputedPosition == 'top' && this.closeBtnColorIfOnImg
          ? this.closeBtnColorIfOnImg
          : this.theme
      updateCloseButtonTheme(btnTheme)
    },

    trackIfUserHasSeenAnImage() {
      if (!window.DYO || !this.heroImageIsVisible) return

      DY.API('event', {
        name: 'Newsletter overlay - image visible',
        properties: { position: this.heroImageComputedPosition },
      })
    },
  },
}
</script>

<style scoped lang="scss">
.popup {
  &__block {
    display: flex;
    position: relative;
    overflow: auto;
    background: $color-white;
    text-align: center;
    opacity: 1;

    @include min-screen($mq-width-small) {
      margin: 0 $site-gutter;
    }

    &--original {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &--dark {
      background: $color-grey-dark-1;
      color: $color-white;
    }

    &--img-top {
      flex-direction: column;
      align-items: center;
    }
  }

  &__close {
    display: inline-block;
    position: absolute;
    top: 0;
    right: 0;
    padding: 25px;
    color: $color-text-lighter;
    cursor: pointer;

    &:hover {
      color: $color-text;
    }
  }

  &__hero-image {
    background-position: 50% 50%;
    background-repeat: no-repeat;
    background-size: cover;

    &--top {
      flex-direction: column;
      width: 100%;
      min-height: 300px;
      height: 40vh;
      max-height: 350px;
    }

    &--left {
      width: 35vw;
      max-width: 440px;
    }
  }

  &__content {
    display: flex;
    flex-grow: 1;
    align-items: center;
    min-height: 330px;

    @include min-screen($mq-width-medium) {
      min-height: 370px;
    }

    &--full-height {
      align-items: baseline;
    }
  }

  &__content-inner {
    padding: 30px 30px 25px;
    width: 100%;

    @include min-screen($mq-width-medium) {
      padding: 45px 50px;
      width: 550px;
    }

    @include min-screen($mq-width-large) {
      width: 600px;
    }

    @include min-screen($mq-width-larger) {
      width: 650px;
    }

    &--img-left {
      @include min-screen($mq-width-medium) {
        width: 550px;
      }

      @include min-screen($mq-width-full) {
        width: 650px;
      }
    }
  }

  &__header {
    margin: 0 20px 10px;
    font-size: $font-size-heading-large + 4;

    &--newsletter {
      font-size: $font-size-xlarge;
      letter-spacing: $font-spacing-neutraface;

      @media ($mq-medium) and ($mq-height-small) {
        font-size: $font-size-heading-normal;
      }
    }

    @include max-screen($mq-width-small) {
      /deep/ .u-nobr {
        white-space: normal;
      }
    }

    @include min-screen($mq-width-medium) {
      margin: 0 10px 10px;
    }
  }

  &__subheader {
    font-family: $font-family-headings;
    font-size: $font-size-larger;
    color: $color-text;

    &--dark {
      color: $color-white;
    }

    &--newsletter {
      @media ($mq-medium) and ($mq-height-small) {
        max-width: 420px;
        margin: 0 auto;
      }
    }
  }

  &__cta-wrap {
    margin-top: 50px;
  }

  $input-height: 45px;
  $input-height-medium: 41px;

  /deep/ &__cta {
    margin: 5px;
    width: auto;
    min-width: 230px;
    font-size: $font-size-large;
    white-space: nowrap;
    user-select: none;

    &--newsletter {
      margin-left: 4px;
      margin-right: 4px;
      padding: 0 20px;
      width: auto;
      min-width: 120px;
      line-height: $input-height-medium + 2;
      font-size: $font-size-larger;
      font-family: $font-family-headings;
      letter-spacing: $font-spacing-neutraface;

      @include min-screen($mq-width-large) {
        line-height: $input-height + 2;
      }

      @include min-screen($mq-width-small) {
        padding: 0 25px;
      }
    }
  }
}
</style>
